import { useState, useEffect, useCallback } from 'react';
import axiosClient from '../axios-client';

/**
 * Custom hook to check YouCan connection status for a user/business.
 * 
 * @param {number|string} userId - The client user ID
 * @param {number|string} businessId - The business ID
 * @returns {{ connected: boolean, storeSlug: string, scopes: string, connectedAt: string, loading: boolean, error: string|null, refetch: function }}
 */
export function useYouCanStatus(userId, businessId) {
    const [data, setData] = useState({
        connected: false,
        storeSlug: '',
        scopes: '',
        connectedAt: null,
        tokenExpiresAt: null,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchStatus = useCallback(async () => {
        if (!userId || !businessId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await axiosClient.get(
                `/api/youcan/status?user_id=${userId}&business=${businessId}`
            );
            setData({
                connected: response.data.connected || false,
                isActive: response.data.is_active || false,
                connectionId: response.data.id || null,
                storeSlug: response.data.store_slug || '',
                scopes: response.data.scopes || '',
                connectedAt: response.data.connected_at || null,
                tokenExpiresAt: response.data.token_expires_at || null,
            });
        } catch (err) {
            if (err.response?.status === 404) {
                // Not connected — not an error
                setData({
                    connected: false,
                    storeSlug: '',
                    scopes: '',
                    connectedAt: null,
                    tokenExpiresAt: null,
                });
            } else {
                setError(err.response?.data?.error || 'Failed to check YouCan status');
            }
        } finally {
            setLoading(false);
        }
    }, [userId, businessId]);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    return { ...data, loading, error, refetch: fetchStatus };
}

export default useYouCanStatus;
